import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Chip from '@mui/material/Chip';
import {
  Box,
  Card,
  Grid,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  CircularProgress,
} from '@mui/material';

import { getExecutionLogList } from 'src/server/store/automation';

import { useTable } from 'src/components/table';
import Scrollbar from 'src/components/scrollbar';
import EmptyContent from 'src/components/empty-content';
import TableSelectedAction from 'src/components/table/table-selected-action';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import AutomationTableHeadCustom from 'src/components/table/automation-table-head-custom';

import SingleViewDialog from './SingleViewDialog';
import ExecutionLogFilters from './ExecutionLogFilters';
import FindingsDetailDialog from './FindingsDetailDialog';

// Utility function to truncate text
// function truncateText(text, maxLength = 50) {
//   if (!text) return '';
//   return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
// }

const TABLE_HEAD = [
  { id: 'no', label: '#' },
  { id: 'automation', label: 'Automation' },
  { id: 'automationFindings', label: 'Findings' },
  { id: 'automationRuleTemplate', label: 'Rule Template' },
  { id: 'executionType', label: 'Execution Type' },
  { id: 'executionStatus', label: 'Status' },
  // { id: 'reason', label: 'Reason' },
  { id: 'performance', label: 'Performance (Before | After)' },
];

export default function ExecutionLogs({ formattedDate }) {
  const dispatch = useDispatch();

  const table = useTable({ defaultOrderBy: 'createdAt', defaultRowsPerPage: 25 });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterObject, setFilterObject] = useState();
  const [sortValue, setSortValue] = useState(-1);
  const [viewOpen, setViewOpen] = useState(false);

  // Get execution log data from Redux store
  const { executionLogList, loading } = useSelector((state) => ({
    executionLogList: state.automation.executionLogList || [],
    loading: state.automation.loading,
  }));

  // Use mapped data
  const executionLogs = (executionLogList?.executionLogs || []).map((item, idx) => ({
    id: item._id || idx,
    automation: item.automation?.name || item.automation || '',
    automationFindings: item.automationFindings?.name || item.automationFindings || '',
    automationRuleTemplate: item.automationRuleTemplate?.title || item.automationRuleTemplate || '',
    executionType: item.executionType || '',
    executionStatus: item.executionStatus || '',
    error: item.error?.message || item.error || '',
    beforeBid: item.performanceMetrics?.beforeExecution?.bid ?? '',
    beforeState: item.performanceMetrics?.beforeExecution?.state ?? '',
    afterBid: item.performanceMetrics?.afterExecution?.bid ?? '',
    afterState: item.performanceMetrics?.afterExecution?.state ?? '',
    reason: item.reason || '',
    raw: item,
  }));

  useEffect(() => {
    const filterDataObj = {
      filter_data: filterObject,
      sort_data: {
        createdAt: sortValue,
      },
    };

    const credentials = {
      state: filterDataObj,
      page: table?.page || 1,
      limit: table?.rowsPerPage || 25,
      search: searchQuery,
      dispatch,
      date: formattedDate,
    };

    dispatch(
      getExecutionLogList({
        credentials,
      })
    );
  }, [
    dispatch,
    filterObject,
    sortValue,
    searchQuery,
    table?.page,
    table?.rowsPerPage,
    formattedDate,
  ]);

  const totalPage = executionLogList?.total;

  // Helper for status chip color
  function getStatusColor(status) {
    if (status === 'PENDING') return 'info';
    if (status === 'SUCCESS') return 'success';
    if (status === 'FAILED') return 'error';
    if (status === 'REJECTED') return 'error';
    return 'default';
  }

  const [findingsModalOpen, setFindingsModalOpen] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);

  const handleViewFinding = (findingsId) => {
    setFindingsModalOpen(true);
    setSelectedFinding({ _id: findingsId });
  };

  return (
    <Card sx={{ padding: '10px 14px 14px' }}>
      <FindingsDetailDialog
        open={findingsModalOpen}
        onClose={() => setFindingsModalOpen(false)}
        data={selectedFinding}
      />

      <Grid mt={2}>
        <ExecutionLogFilters
          getSearchPlaceholder={() => 'Search Execution, Rule'}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setFilterObject={setFilterObject}
          sortValue={sortValue}
          setSortValue={setSortValue}
          sort={null}
        />
      </Grid>
      <Stack spacing={3} pt={3}>
        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          <TableSelectedAction
            dense={table.dense}
            numSelected={table.selected?.length}
            rowCount={executionLogList?.total}
            onSelectAllRows={(checked) => {
              table.onSelectAllRows(
                checked,
                executionLogs.map((row) => row.id)
              );
            }}
          />
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'}>
              <AutomationTableHeadCustom
                order={table.order}
                orderBy={table.orderBy}
                headLabel={TABLE_HEAD}
                rowCount={executionLogList?.total}
                numSelected={table.selected?.length}
                onSort={table.onSort}
              />
              {loading ? (
                <TableRow>
                  <TableCell align="center" colSpan={12} sx={{ py: 3 }}>
                    <Paper sx={{ textAlign: 'center' }}>
                      <CircularProgress color="inherit" />
                    </Paper>
                  </TableCell>
                </TableRow>
              ) : (
                <TableBody>
                  {executionLogs?.map((row, index) => (
                    <TableRow hover key={row.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.automation?.title || '---'}</TableCell>
                      <TableCell>
                        <Button
                          variant="text"
                          size="small"
                          color="primary"
                          sx={{ px: 1, fontSize: '12px' }}
                          onClick={() => handleViewFinding(row?.automationFindings?._id)}
                        >
                          View Findings
                        </Button>
                      </TableCell>
                      <TableCell>{row.automationRuleTemplate || '---'}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.executionType}
                          color={row.executionType === 'AUTOMATIC' ? 'success' : 'default'}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.executionStatus}
                          color={getStatusColor(row.executionStatus)}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                          variant="outlined"
                        />
                      </TableCell>
                      {/* <TableCell>
                        <Tooltip title={row.reason || ''} placement="top" arrow>
                          <span>{truncateText(row.reason, 60) || '---'}</span>
                        </Tooltip>
                      </TableCell> */}
                      <TableCell>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1,
                            display: 'flex',
                            gap: 2,
                            minWidth: 350,
                            background: '#f9f9f9',
                          }}
                        >
                          <Box sx={{ minWidth: 160 }}>
                            <Typography variant="caption" fontWeight={600}>
                              Bid :{' '}
                              <span style={{ color: '#1976d2' }}>
                                {row.beforeBid !== undefined && row.beforeBid !== ''
                                  ? row.beforeBid
                                  : '--'}
                              </span>
                            </Typography>
                            ,{' '}
                            <Typography variant="caption" fontWeight={600}>
                              State :{' '}
                              <span style={{ color: '#1976d2' }}>{row.beforeState || '--'}</span>
                            </Typography>
                          </Box>

                          <Box sx={{ width: 0.5, borderLeft: '1px solid #eee', mx: 1 }} />
                          <Box sx={{ minWidth: 150 }}>
                            <Typography variant="caption" fontWeight={600}>
                              Bid :{' '}
                              <span style={{ color: '#43a047' }}>
                                {row.afterBid !== undefined && row.afterBid !== ''
                                  ? row.afterBid
                                  : '--'}
                              </span>
                            </Typography>
                            ,{' '}
                            <Typography variant="caption" fontWeight={600}>
                              State :{' '}
                              <span style={{ color: '#43a047' }}>{row.afterState || '--'}</span>
                            </Typography>
                          </Box>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </Scrollbar>
          {totalPage === 0 && (
            <Stack sx={{ p: 2, textAlign: 'center' }}>
              <EmptyContent
                filled
                title=""
                sx={{
                  py: 10,
                  width: '100%',
                }}
              />
            </Stack>
          )}
        </TableContainer>

        <TablePaginationCustom
          count={totalPage || 0}
          page={table.page}
          customRowsPerPage={25}
          rowsPerPage={table.rowsPerPage || 25}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense
        />
      </Stack>
      <SingleViewDialog open={viewOpen} onClose={() => setViewOpen(false)} type="execution" />
    </Card>
  );
}
ExecutionLogs.propTypes = {
  formattedDate: PropTypes.any,
};
